import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service layer for caregiver alert management
 * Handles alert detection, email notifications, and alert status updates
 */

export const alertService = {
  /**
   * Get pending alerts for current caregiver
   * @param {object} options - Query options (limit, status)
   * @returns {Promise<{data, error}>}
   */
  async getPendingAlerts(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      let query = supabase
        ?.from('caregiver_alerts')
        ?.select(`
          *,
          managed_profiles!inner(name, relationship),
          symptom_entries!inner(
            feeling_level,
            symptom_categories,
            severity,
            daily_impacts,
            notes,
            created_at
          )
        `)
        ?.eq('caregiver_id', user?.id)
        ?.order('triggered_at', { ascending: false });
      
      if (options?.status) {
        query = query?.eq('alert_status', options?.status);
      } else {
        query = query?.in('alert_status', ['pending', 'sent']);
      }
      
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger?.error('Failed to fetch pending alerts', error, { requestId, userId: user?.id });
        return { data: null, error };
      }
      
      // Convert snake_case to camelCase
      const alerts = data?.map(alert => ({
        id: alert?.id,
        caregiverId: alert?.caregiver_id,
        managedProfileId: alert?.managed_profile_id,
        symptomEntryId: alert?.symptom_entry_id,
        alertSeverity: alert?.alert_severity,
        alertStatus: alert?.alert_status,
        triggeredAt: alert?.triggered_at,
        sentAt: alert?.sent_at,
        acknowledgedAt: alert?.acknowledged_at,
        resolvedAt: alert?.resolved_at,
        notes: alert?.notes,
        dependentName: alert?.managed_profiles?.name,
        relationship: alert?.managed_profiles?.relationship,
        symptomDetails: {
          feelingLevel: alert?.symptom_entries?.feeling_level,
          symptomCategories: alert?.symptom_entries?.symptom_categories,
          severity: alert?.symptom_entries?.severity,
          dailyImpacts: alert?.symptom_entries?.daily_impacts,
          notes: alert?.symptom_entries?.notes,
          timestamp: alert?.symptom_entries?.created_at
        }
      }));
      
      logger?.debug('Pending alerts fetched', { requestId, count: alerts?.length });
      return { data: alerts, error: null };
    } catch (error) {
      logger?.error('Pending alerts fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch alerts' } };
    }
  },

  /**
   * Send alert email to caregiver
   * @param {string} alertId - Alert ID
   * @returns {Promise<{data, error}>}
   */
  async sendAlertEmail(alertId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      // Get alert details
      const { data: alert, error: alertError } = await supabase
        ?.from('caregiver_alerts')
        ?.select(`
          *,
          managed_profiles!inner(name, relationship),
          symptom_entries!inner(
            feeling_level,
            symptom_categories,
            severity,
            daily_impacts,
            notes,
            created_at
          )
        `)
        ?.eq('id', alertId)
        ?.eq('caregiver_id', user?.id)
        ?.single();
      
      if (alertError) {
        logger?.error('Failed to fetch alert details', alertError, { requestId, alertId });
        return { data: null, error: alertError };
      }
      
      // Get caregiver profile for email
      const { data: caregiverProfile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, first_name, last_name')
        ?.eq('id', user?.id)
        ?.single();
      
      if (profileError) {
        logger?.error('Failed to fetch caregiver profile', profileError, { requestId });
        return { data: null, error: profileError };
      }
      
      const caregiverName = `${caregiverProfile?.first_name || ''} ${caregiverProfile?.last_name || ''}`?.trim() || 'there';
      
      // Send email via edge function
      const { data: emailData, error: emailError } = await supabase?.functions?.invoke('send-caregiver-alert', {
        body: {
          caregiverEmail: caregiverProfile?.email,
          caregiverName: caregiverName,
          dependentName: alert?.managed_profiles?.name,
          symptomDetails: {
            severity: alert?.symptom_entries?.severity,
            symptoms: alert?.symptom_entries?.symptom_categories,
            dailyImpacts: alert?.symptom_entries?.daily_impacts,
            notes: alert?.symptom_entries?.notes,
            timestamp: alert?.symptom_entries?.created_at
          },
          alertSeverity: alert?.alert_severity,
          alertId: alertId
        }
      });
      
      if (emailError) {
        logger?.error('Failed to send alert email', emailError, { requestId, alertId });
        return { data: null, error: emailError };
      }
      
      // Update alert status to sent
      const { error: updateError } = await supabase
        ?.from('caregiver_alerts')
        ?.update({
          alert_status: 'sent',
          sent_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', alertId)
        ?.eq('caregiver_id', user?.id);
      
      if (updateError) {
        logger?.error('Failed to update alert status', updateError, { requestId, alertId });
      }
      
      logger?.audit('ALERT_EMAIL_SENT', user?.id, { requestId, alertId });
      return { data: emailData, error: null };
    } catch (error) {
      logger?.error('Alert email send error', error, { requestId, alertId });
      return { data: null, error: { message: 'Failed to send alert email' } };
    }
  },

  /**
   * Process pending alerts and send emails
   * @returns {Promise<{data, error}>}
   */
  async processPendingAlerts() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      // Get all pending alerts
      const { data: alerts, error: alertsError } = await this.getPendingAlerts({ status: 'pending' });
      
      if (alertsError) {
        return { data: null, error: alertsError };
      }
      
      if (!alerts || alerts?.length === 0) {
        return { data: { processed: 0 }, error: null };
      }
      
      // Send emails for each pending alert
      const results = await Promise.allSettled(
        alerts?.map(alert => this.sendAlertEmail(alert?.id))
      );
      
      const successCount = results?.filter(r => r?.status === 'fulfilled')?.length;
      
      logger?.debug('Pending alerts processed', { requestId, total: alerts?.length, success: successCount });
      return { data: { processed: successCount, total: alerts?.length }, error: null };
    } catch (error) {
      logger?.error('Process pending alerts error', error, { requestId });
      return { data: null, error: { message: 'Failed to process pending alerts' } };
    }
  },

  /**
   * Acknowledge an alert
   * @param {string} alertId - Alert ID
   * @returns {Promise<{data, error}>}
   */
  async acknowledgeAlert(alertId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      const { data, error } = await supabase
        ?.from('caregiver_alerts')
        ?.update({
          alert_status: 'acknowledged',
          acknowledged_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', alertId)
        ?.eq('caregiver_id', user?.id)
        ?.select()
        ?.single();
      
      if (error) {
        logger?.error('Failed to acknowledge alert', error, { requestId, alertId });
        return { data: null, error };
      }
      
      logger?.audit('ALERT_ACKNOWLEDGED', user?.id, { requestId, alertId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Alert acknowledgement error', error, { requestId, alertId });
      return { data: null, error: { message: 'Failed to acknowledge alert' } };
    }
  },

  /**
   * Resolve an alert
   * @param {string} alertId - Alert ID
   * @param {string} notes - Resolution notes
   * @returns {Promise<{data, error}>}
   */
  async resolveAlert(alertId, notes = '') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      const { data, error } = await supabase
        ?.from('caregiver_alerts')
        ?.update({
          alert_status: 'resolved',
          resolved_at: new Date()?.toISOString(),
          notes: notes,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', alertId)
        ?.eq('caregiver_id', user?.id)
        ?.select()
        ?.single();
      
      if (error) {
        logger?.error('Failed to resolve alert', error, { requestId, alertId });
        return { data: null, error };
      }
      
      logger?.audit('ALERT_RESOLVED', user?.id, { requestId, alertId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Alert resolution error', error, { requestId, alertId });
      return { data: null, error: { message: 'Failed to resolve alert' } };
    }
  },

  /**
   * Get alert statistics for caregiver
   * @returns {Promise<{data, error}>}
   */
  async getAlertStats() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      const { data, error } = await supabase
        ?.from('caregiver_alerts')
        ?.select('alert_status, alert_severity')
        ?.eq('caregiver_id', user?.id);
      
      if (error) {
        logger?.error('Failed to fetch alert stats', error, { requestId });
        return { data: null, error };
      }
      
      const stats = {
        total: data?.length || 0,
        pending: data?.filter(a => a?.alert_status === 'pending')?.length || 0,
        sent: data?.filter(a => a?.alert_status === 'sent')?.length || 0,
        acknowledged: data?.filter(a => a?.alert_status === 'acknowledged')?.length || 0,
        resolved: data?.filter(a => a?.alert_status === 'resolved')?.length || 0,
        critical: data?.filter(a => a?.alert_severity === 'critical')?.length || 0,
        high: data?.filter(a => a?.alert_severity === 'high')?.length || 0
      };
      
      logger?.debug('Alert stats fetched', { requestId, stats });
      return { data: stats, error: null };
    } catch (error) {
      logger?.error('Alert stats fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch alert stats' } };
    }
  }
};

export default alertService;